import { 
  createUser, 
  getUserByClerkId, 
  getUserByEmail, 
  updateUser, 
  deactivateUser, 
  getActiveUsers,
  getAllUsers,
  createAdminRequest,
  approveAdminRequest,
  type CreateUserData,
  type UpdateUserData
} from '../lib/db/operations';

// Tipos para las respuestas de la API
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  isNewUser?: boolean;
}

// Clase para manejar las operaciones de la API de usuarios
export class UsersAPI {
  
  // Crear un nuevo usuario
  static async create(userData: CreateUserData): Promise<ApiResponse> {
    try {
      // Validar datos requeridos
      if (!userData.clerkId || !userData.email) {
        return {
          success: false,
          error: 'Clerk ID and email are required'
        };
      }

      // Verificar si el usuario ya existe
      const existingUser = await getUserByClerkId(userData.clerkId);
      if (existingUser.success && existingUser.data) {
        return {
          success: false,
          error: 'User already exists'
        };
      }

      // Crear el usuario
      const result = await createUser(userData);
      
      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: 'User created successfully'
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error('API Error creating user:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }

  // Obtener usuario por Clerk ID
  static async getByClerkId(clerkId: string): Promise<ApiResponse> {
    try {
      if (!clerkId) {
        return {
          success: false,
          error: 'Clerk ID is required'
        };
      }

      const result = await getUserByClerkId(clerkId);
      
      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: result.data ? 'User found' : 'User not found'
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error('API Error fetching user by Clerk ID:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }

  // Obtener usuario por email
  static async getByEmail(email: string): Promise<ApiResponse> {
    try {
      if (!email) {
        return {
          success: false,
          error: 'Email is required'
        };
      }

      const result = await getUserByEmail(email);
      
      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: result.data ? 'User found' : 'User not found'
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error('API Error fetching user by email:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }

  // Actualizar usuario
  static async update(clerkId: string, updateData: UpdateUserData): Promise<ApiResponse> {
    try {
      if (!clerkId) {
        return {
          success: false,
          error: 'Clerk ID is required'
        };
      }

      // Verificar que el usuario existe
      const existingUser = await getUserByClerkId(clerkId);
      if (!existingUser.success || !existingUser.data) {
        return {
          success: false,
          error: 'User not found'
        };
      }

      const result = await updateUser(clerkId, updateData);
      
      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: 'User updated successfully'
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error('API Error updating user:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }

  // Desactivar usuario
  static async deactivate(clerkId: string): Promise<ApiResponse> {
    try {
      if (!clerkId) {
        return {
          success: false,
          error: 'Clerk ID is required'
        };
      }

      const result = await deactivateUser(clerkId);
      
      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: 'User deactivated successfully'
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error('API Error deactivating user:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }

  // Obtener todos los usuarios activos
  static async getActive(): Promise<ApiResponse> {
    try {
      const result = await getActiveUsers();
      
      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: `Found ${result.data?.length || 0} active users`
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error('API Error fetching active users:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }

  // Obtener todos los usuarios (Admin)
  static async getAll(): Promise<ApiResponse> {
    try {
      const result = await getAllUsers();
      
      if (result.success) {
        return {
          success: true,
          data: result.data,
          message: `Found ${result.data?.length || 0} users`
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error('API Error fetching all users:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }

  // Sincronizar usuario desde Clerk
  static async syncFromClerk(clerkUserData: {
    id: string;
    emailAddresses: Array<{ emailAddress: string }>;
    firstName?: string;
    lastName?: string;
    imageUrl?: string;
  }): Promise<ApiResponse> {
    try {
      const userData: CreateUserData = {
        clerkId: clerkUserData.id,
        email: clerkUserData.emailAddresses[0]?.emailAddress || '',
        firstName: clerkUserData.firstName || undefined,
        lastName: clerkUserData.lastName || undefined,
        imageUrl: clerkUserData.imageUrl || undefined,
      };

      // Verificar si el usuario ya existe
      const existingUser = await getUserByClerkId(userData.clerkId);
      
      if (existingUser.success && existingUser.data) {
        // Actualizar usuario existente
        const updateResult = await updateUser(userData.clerkId, {
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          imageUrl: userData.imageUrl,
        });
        
        return {
          success: updateResult.success,
          data: updateResult.data,
          error: updateResult.error,
          message: updateResult.success ? 'User synchronized successfully' : undefined,
          isNewUser: false
        };
      } else {
        // Crear nuevo usuario
        const createResult = await createUser(userData);
        
        return {
          success: createResult.success,
          data: createResult.data,
          error: createResult.error,
          message: createResult.success ? 'User created and synchronized successfully' : undefined,
          isNewUser: true
        };
      }
    } catch (error) {
      console.error('API Error syncing user from Clerk:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }

  // Solicitar ser admin
  static async requestAdmin(targetClerkId: string, requesterClerkId: string, targetName: string, targetEmail: string): Promise<ApiResponse> {
    try {
      // 1. Crear el registro en base de datos
      const result = await createAdminRequest(targetClerkId, requesterClerkId);
      
      if (result.success && result.data) {
        // 2. Enviar email de aprobación (llamada al Backend)
        try {
          await fetch('/api/send-approval-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              targetName,
              targetEmail,
              requestId: result.data.id
            })
          });
        } catch (e) {
          console.error('Error sending approval email:', e);
          // No fallamos toda la operación si el mail falla, pero lo logueamos
        }

        return {
          success: true,
          data: result.data,
          message: 'Admin request created successfully'
        };
      } else {
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error('API Error creating admin request:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }

  // Aprobar solicitud
  static async approveRequest(requestId: string): Promise<ApiResponse> {
    try {
      const result = await approveAdminRequest(requestId);
      return result;
    } catch (error) {
      console.error('API Error approving request:', error);
      return {
        success: false,
        error: 'Internal server error'
      };
    }
  }
}